import { useEffect, useMemo, useState } from "react";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { Globe2 } from "lucide-react";
import { api } from "@/lib/api";
import world from "world-atlas/countries-110m.json";

const W = 960;
const H = 470;
const land = feature(world, world.objects.countries);
const projection = geoNaturalEarth1().fitSize([W, H], land);
const path = geoPath(projection);
const landPaths = land.features.map((f) => path(f));

export const FraudHeatmap = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/dashboard/heatmap").then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const points = data?.points || [];
  const max = points[0]?.blocked || 1;
  const markers = useMemo(
    () => points.map((p) => ({ ...p, xy: projection([p.lng, p.lat]), r: 4 + Math.sqrt(p.blocked / max) * 12 })),
    [points, max]
  );

  return (
    <div data-testid="fraud-heatmap" className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <Globe2 className="w-4 h-4 text-[#FF3B5C]" />
        <h3 className="font-display text-lg text-white">Fraud origins — live world map</h3>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wider uppercase text-[#FF3B5C]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B5C] animate-pulse" /> blocked clicks · 30d
        </span>
        {data && (
          <span data-testid="heatmap-data-source" className="ml-auto font-mono text-[11px] tracking-wider uppercase text-slate-400">
            {data.data_source === "live" ? "your live traffic" : "demo data"}
          </span>
        )}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="World map of blocked click origins">
        {landPaths.map((d, i) => (
          <path key={i} d={d} fill="#0E1626" stroke="#1E2A3E" strokeWidth="0.6" />
        ))}
        {markers.map((m, i) =>
          m.xy ? (
            <g key={m.code} data-testid={`heatmap-marker-${m.code}`}>
              <circle cx={m.xy[0]} cy={m.xy[1]} r={m.r} fill="#FF3B5C" fillOpacity="0.5">
                <animate attributeName="r" values={`${m.r};${m.r * 2.4}`} dur="2s" begin={`${(i % 6) * 0.33}s`} repeatCount="indefinite" />
                <animate attributeName="fill-opacity" values="0.45;0" dur="2s" begin={`${(i % 6) * 0.33}s`} repeatCount="indefinite" />
              </circle>
              <circle cx={m.xy[0]} cy={m.xy[1]} r={m.r * 0.55} fill="#FF3B5C" stroke="#040914" strokeWidth="1">
                <title>{`${m.country} — ${m.blocked.toLocaleString()} blocked`}</title>
              </circle>
            </g>
          ) : null
        )}
      </svg>

      {points.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {points.slice(0, 5).map((p, i) => (
            <span
              key={p.code}
              data-testid={`heatmap-top-${i}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#040914] border border-[#FF3B5C]/20 font-mono text-[11px] text-slate-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B5C]" />
              {p.country} <span className="text-[#FF3B5C]">{p.blocked.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
